import { diffWordsWithSpace } from "diff";
import { type FC, useMemo } from "react";
import { twMerge } from "tailwind-merge";

interface FieldDiffViewProps {
  savedValue: string;
  editedValue: string;
  className?: string;
}

interface DiffPart {
  type: "added" | "removed" | "unchanged";
  text: string;
}

function computeWordDiff(savedValue: string, editedValue: string): DiffPart[] {
  const changes = diffWordsWithSpace(savedValue, editedValue);
  return changes.map((change) => ({
    type: change.added
      ? "added"
      : change.removed
        ? "removed"
        : ("unchanged" as const),
    text: change.value,
  }));
}

export const FieldDiffView: FC<FieldDiffViewProps> = ({
  savedValue,
  editedValue,
  className,
}) => {
  const parts = useMemo(
    () => computeWordDiff(savedValue, editedValue),
    [savedValue, editedValue],
  );

  // Fallback for callers that mount this component unconditionally. Callers
  // that swap between an editable input and the diff view only mount this when
  // savedValue !== editedValue, so for them this branch is unreachable.
  if (savedValue === editedValue) {
    return (
      <div
        className={twMerge(
          "rounded-md bg-gray-50 px-3 py-1.5 text-sm leading-6 text-muted-foreground shadow-xs ring-1 ring-border-strong ring-inset",
          className,
        )}
      >
        No changes
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        "rounded-md bg-white px-3 py-1.5 text-sm leading-6 break-words whitespace-pre-wrap text-foreground shadow-xs ring-1 ring-border-strong ring-inset",
        className,
      )}
    >
      {parts.map((part, index) => {
        if (part.type === "unchanged") {
          return <span key={index}>{part.text}</span>;
        }
        return (
          <span
            key={index}
            className={twMerge(
              part.type === "added" && "bg-green-100 text-green-900",
              part.type === "removed" &&
                "bg-red-100 text-red-900 line-through decoration-red-700/60",
            )}
          >
            {part.text}
          </span>
        );
      })}
    </div>
  );
};
